import React from 'react';
import { SxProps } from '@mui/system';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

import logo from '../images/skipify_horizontal_logo.svg';
import AccountInfo from './components/AccountInfo';
import PartnerSelect from './components/PartnerSelect';
import { useMerchant } from '../contexts/MerchantContext';
import { getAppBarHeight } from '../util/helpers';
import appTheme from '../styles/themes/theme';
import TabBar from './TabBar';
import { useMerchantAwareNavigate } from '../hooks/useMerchantAwareNavigate';

type Props = {
  sx?: SxProps;
};

type HeaderBarProps = {
  children?: React.ReactNode;
  sx?: SxProps;
};

const HeaderBar = ({ sx, children }: HeaderBarProps) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      backgroundColor: 'white',
      borderBottom: (theme) => `1px solid ${theme.palette.grey[300]}`,
      py: 0,
      px: { xs: 2, md: 4 },
      width: '100%',
      boxSizing: 'border-box',
      height: getAppBarHeight(),
      ...sx,
    }}
  >
    {children}
  </Box>
);

const Logo = () => {
  const navigate = useMerchantAwareNavigate();
  const { getHomePath } = useMerchant();
  return (
    <Box
      component="img"
      src={logo}
      alt="logo"
      onClick={() => navigate(getHomePath())}
      sx={{
        width: 92,
        height: 22,
        marginRight: 2,
        cursor: 'pointer',
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    />
  );
};

const NameAndLogo = ({ name }: { name?: string }) => (
  <Stack direction="row" alignItems="center">
    <Logo />
    {!!name?.length && (
      <Typography
        component="div"
        variant="large"
        sx={{ borderLeft: `1px solid`, borderColor: 'border.main', pl: 2 }}
      >
        {name}
      </Typography>
    )}
  </Stack>
);

const Header: React.FC<Props> = ({ sx }) => {
  const { logout, isChildMerchantRestrictedShell } = useMerchant();
  const { isAuthenticated } = useAuth0();

  const isMobileView = useMediaQuery(appTheme.breakpoints.down('md'));

  if (!isAuthenticated) {
    return (
      <HeaderBar sx={{ justifyContent: 'space-between' }}>
        <NameAndLogo name="Skipify Merchant Portal" />
      </HeaderBar>
    );
  }

  if (isMobileView) {
    return (
      <Stack>
        <HeaderBar sx={{ justifyContent: 'space-between' }}>
          <Logo />
          <AccountInfo logout={logout} />
        </HeaderBar>
        {!isChildMerchantRestrictedShell && (
          <HeaderBar>
            <PartnerSelect sx={{ width: '100%' }} />
          </HeaderBar>
        )}
        <HeaderBar>
          <TabBar />
        </HeaderBar>
      </Stack>
    );
  }

  return (
    <HeaderBar
      sx={{
        justifyContent: 'initial',
        ...sx,
      }}
    >
      <Logo />
      {!isChildMerchantRestrictedShell && <PartnerSelect />}
      <TabBar sx={{ ml: 4 }} />
      <Box sx={{ display: 'flex', justifySelf: 'flex-end', gap: 2, ml: 'auto', pl: 4 }}>
        <AccountInfo logout={logout} />
      </Box>
    </HeaderBar>
  );
};

export default Header;
